import { apiClient } from '@/lib/api/apiClient';
import { endpoints } from '@/lib/api/apiEndpoints';

export interface QuestionDto {
  id: string;
  questionText: string;
  questionTextAr?: string;
  difficulty: string;
  categoryId: string;
  categoryName: string;
  options: AnswerOptionDto[];
  correctAnswerIndex: number;
  explanation?: string;
  explanationAr?: string;
  imageUrl?: string;
  imageSize?: 'small' | 'medium' | 'large';
  timeLimit?: number;
  isActive: boolean;
  enableForNewUsers?: boolean;
  isPromotionQuestion?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface AnswerOptionDto {
  text: string;
  textAr?: string;
  isCorrect: boolean;
}

export interface CreateQuestionRequest {
  categoryId: string;
  questionText: string;
  questionTextAr?: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  options: AnswerOptionDto[];
  timeLimit?: number;
  explanation?: string;
  explanationAr?: string;
  imageUrl?: string;
  imageSize?: 'small' | 'medium' | 'large';
  isActive?: boolean;
  enableForNewUsers?: boolean;
  isPromotionQuestion?: boolean;
  tags?: string[];
}

export interface UpdateQuestionRequest extends CreateQuestionRequest {
  questionId: string;
}

export interface PagedResult<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
}

export const questionService = {
  async getAllQuestions(params?: {
    categoryId?: string;
    difficulty?: string;
    isActive?: boolean;
    enableForNewUsers?: boolean | null;
    isPromotionQuestion?: boolean | null;
    searchTerm?: string;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<PagedResult<QuestionDto>> {
    const response = await apiClient.get(endpoints.admin.questions(), { params });
    return response.data.data;
  },

  async bulkUpdateEnableForNewUsers(questionIds: string[], enable: boolean): Promise<{ updatedCount: number }> {
    const response = await apiClient.post(`${endpoints.admin.questions()}/bulk-enable-for-new-users`, {
      questionIds: questionIds.filter(Boolean),
      enable,
    });
    const data = response.data?.data ?? response.data?.Data ?? response.data;
    return { updatedCount: data?.updatedCount ?? data?.UpdatedCount ?? 0 };
  },

  async bulkUpdateIsPromotion(questionIds: string[], isPromotion: boolean): Promise<{ updatedCount: number }> {
    const response = await apiClient.post(`${endpoints.admin.questions()}/bulk-is-promotion`, {
      questionIds: questionIds.filter(Boolean),
      isPromotion,
    });
    const data = response.data?.data ?? response.data?.Data ?? response.data;
    return { updatedCount: data?.updatedCount ?? data?.UpdatedCount ?? 0 };
  },

  async bulkUpdateIsActive(questionIds: string[], isActive: boolean): Promise<{ updatedCount: number }> {
    const response = await apiClient.post(`${endpoints.admin.questions()}/bulk-activate`, {
      questionIds: questionIds.filter(Boolean),
      isActive,
    });
    const data = response.data?.data ?? response.data?.Data ?? response.data;
    return { updatedCount: data?.updatedCount ?? data?.UpdatedCount ?? 0 };
  },

  async getQuestionById(id: string): Promise<QuestionDto> {
    const response = await apiClient.get(endpoints.admin.question(id));
    return response.data.data;
  },

  async createQuestion(data: CreateQuestionRequest): Promise<{ questionId: string }> {
    const response = await apiClient.post(endpoints.admin.questions(), data);
    const d = response.data?.data ?? response.data?.Data ?? response.data ?? {};
    const questionId = d.questionId ?? d.QuestionId ?? '';
    return { questionId: typeof questionId === 'string' ? questionId : String(questionId ?? '') };
  },

  async updateQuestion(id: string, data: UpdateQuestionRequest): Promise<void> {
    await apiClient.put(endpoints.admin.question(id), data);
  },

  async deleteQuestion(id: string): Promise<void> {
    await apiClient.delete(endpoints.admin.question(id));
  },

  async reviewQuestion(id: string, status: 'Approved' | 'Rejected'): Promise<void> {
    await apiClient.patch(`${endpoints.admin.question(id)}/review`, { status });
  },

  async bulkDeleteQuestions(questionIds: string[]): Promise<{ deletedCount: number; notFoundCount: number }> {
    const response = await apiClient.post(`${endpoints.admin.questions()}/bulk-delete`, {
      questionIds: questionIds.filter(Boolean),
    });
    const data = response.data?.data ?? response.data?.Data ?? response.data;
    return {
      deletedCount: data?.deletedCount ?? data?.DeletedCount ?? 0,
      notFoundCount: data?.notFoundCount ?? data?.NotFoundCount ?? 0,
    };
  },
};
